import useSWR from 'swr';
import type { LocalDate } from '@webapp/bff/src/types/dates';
import { SiteDetails } from '@webapp/bff/src/types/site';
import type { DataType } from '@webapp/bff';
import { trpc } from '../config/trpc';
import { getSiteDetailsKey } from './sharedKeys';
import { useContextSite } from '../router/context/ContextSite';

export type SiteInfo = SiteDetails & {
  startDate?: number;
  startPollingDate?: number;
  startMonitoringDate?: number;
  endPollingDate?: number;
  dataCollected?: boolean;
  collectedDataTypes?: DataType[];
  delayKPI?: number;
  queueKPI?: number;
  speedKPI?: number;
  busJourneyTimeKPI?: number;
  busDelayKPI?: number;
  busSiteImpactKPI?: number;
  busAcquiredDelayKPI?: number;
};

export default function useSiteData(
  siteParams: null | {
    orgId: string;
    siteId?: string;
  }
) {
  const { orgId, siteId } = siteParams ?? {};
  const { data, error, mutate, isValidating } = useSWR<SiteInfo>(
    siteId && orgId ? getSiteDetailsKey(siteId) : null,
    async () => {
      if (!siteId || !orgId) {
        throw new Error('Missing siteParams from useSiteData');
      }
      const siteData: SiteInfo = await trpc.site.getSiteDetails.query({
        siteId,
      });
      const pollingDates = await trpc.site.getMonitoringInfo.query({
        orgId,
        siteId,
      });
      const collectedDataTypes = pollingDates.availableTypes;
      const completedSetCollectedDataTypes = collectedDataTypes.includes(
        'SITE_IMPACT_DELAY'
      )
        ? collectedDataTypes
        : ['SITE_IMPACT_DELAY' as const, ...collectedDataTypes];
      siteData.startDate = (pollingDates.startTime ?? 0) / 1000;
      siteData.startPollingDate = (pollingDates.startTime ?? 0) / 1000;
      siteData.startMonitoringDate =
        (pollingDates.startMonitoringDate ?? 0) / 1000;
      siteData.endPollingDate = (pollingDates.endTime ?? 0) / 1000;
      siteData.dataCollected = pollingDates.endTime !== undefined;
      siteData.collectedDataTypes = completedSetCollectedDataTypes;
      siteData.delayKPI = pollingDates.kpi.delay ?? 0;
      siteData.queueKPI = pollingDates.kpi.queue ?? 0;
      siteData.speedKPI = pollingDates.kpi.speed ?? 0;
      siteData.busJourneyTimeKPI = pollingDates.kpi.busJourneyTime ?? 0;
      siteData.busDelayKPI = pollingDates.kpi.busDelay ?? 0;
      siteData.busSiteImpactKPI = pollingDates.kpi.busSiteImpact ?? 0;
      siteData.busAcquiredDelayKPI = pollingDates.kpi.busAcquiredDelay ?? 0;
      return siteData;
    },
    { revalidateOnFocus: false }
  );
  const siteHasQueue =
    data?.collectedDataTypes?.includes('QUEUE_LENGTH') ?? false;
  const mutateThenUpdate =
    (serverOp) =>
    async (
      ...args: Parameters<typeof serverOp>
    ): Promise<ReturnType<typeof serverOp>> => {
      const response = await serverOp(...args);
      await mutate();
      return response;
    };
  return {
    data,
    siteHasQueue,
    error,
    mutate,
    isValidating,
    updateSiteOccupationDates: async (occupationDates: {
      startDate?: LocalDate;
      endDate?: LocalDate;
    }) => {
      if (occupationDates.startDate && occupationDates.endDate) {
        await mutateThenUpdate(trpc.site.updateOccupationDates.mutate)({
          siteId: String(siteId),
          occupationStartDate: occupationDates.startDate,
          occupationEndDate: occupationDates.endDate,
        });
      } else {
        await mutateThenUpdate(trpc.site.removeOccupationDates.mutate)({
          siteId: String(siteId),
        });
      }
    },
    updateSiteDisruptionArea: async (disruptionArea: string) => {
      if (data) {
        mutate(
          {
            ...data,
            disruptionArea,
          },
          {
            revalidate: false,
          }
        );
        // TODO: revert data if optimistic update fails. Also need to consider how unsaved changes are detected.
        return trpc.site.updateDisruptionArea.mutate({
          siteId: String(siteId),
          disruptionArea,
        });
      }
      return mutateThenUpdate(trpc.site.updateDisruptionArea.mutate)({
        siteId: String(siteId),
        disruptionArea,
      });
    },
  };
}
export function useCurrentSiteData() {
  const { orgId, siteId } = useContextSite();
  return useSiteData({
    orgId,
    siteId,
  });
}
